import { formatCurrency } from '../../utils/format';
import type { PortalObligation } from '../../types';
import { dueDayPassed } from '../../utils/israelTime';

/**
 * Portal tabs (T-51, PRD §6.10): five calm sections instead of nine. The
 * active one lives in the URL (?tab=...). Old tab names keep working as
 * aliases (notifications, pending actions and e-mails deep-link to them):
 * `pending` → overview, `milestones` / `hours` → engagements, `quotes` →
 * documents, `approvals` (the customer ticket approver's queue) → requests;
 * the alias also names the section to scroll to.
 */
export type PortalTab = 'overview' | 'requests' | 'conversations' | 'engagements' | 'documents' | 'payments' | 'meetings' | 'users' | 'profile';

/** A section inside a merged tab (deep-link target). */
export type PortalSectionKey = 'pending' | 'milestones' | 'hours' | 'quotes' | 'approvals';

// Q-DQ-114: 'profile' = the customer's "הפרופיל שלי" (always visible to a customer; the section inside it lives in `?ptab=`).
export const PORTAL_TABS: readonly PortalTab[] = ['overview', 'requests', 'conversations', 'engagements', 'documents', 'payments', 'meetings', 'users', 'profile'];

const ALIASES: Record<PortalSectionKey, PortalTab> = {
  pending: 'overview',
  milestones: 'engagements',
  hours: 'engagements',
  quotes: 'documents',
  approvals: 'requests',
};

/** `?tab=` → the tab on screen (aliases resolved; unknown or hidden → overview). */
export function resolvePortalTab(param: string | null | undefined, visible: readonly PortalTab[]): PortalTab {
  const key = param ?? '';
  const tab = (PORTAL_TABS as readonly string[]).includes(key)
    ? (key as PortalTab)
    : isSectionKey(key) ? ALIASES[key] : 'overview';
  return visible.includes(tab) ? tab : 'overview';
}

/**
 * Q-DQ-114: where a customer's /profile link goes - the portal's "הפרופיל שלי". The old `?tab=` (account /
 * notifications) becomes the section inside it (`?ptab=`), so it does not collide with the portal's own `?tab=`.
 */
export function profileRedirectTarget(search: string): string {
  const old = new URLSearchParams(search).get('tab');
  return old ? `/portal?tab=profile&ptab=${encodeURIComponent(old)}` : '/portal?tab=profile';
}

/** `?tab=` → the section a legacy tab name points at, or null. */
export function portalSectionFromParam(param: string | null | undefined): PortalSectionKey | null {
  return param && isSectionKey(param) ? param : null;
}

function isSectionKey(key: string): key is PortalSectionKey {
  return Object.prototype.hasOwnProperty.call(ALIASES, key);
}

/** DOM id of a section (scroll target for deep links). */
export const portalSectionId = (key: PortalSectionKey) => `portal-section-${key}`;

// ---------------------------------------------------------------------------
// P-19b (D-146) — service-request statuses: re-exported from the single
// source of truth utils/ticketStatus.shared.ts (unified at MERGE-INT7).
// ---------------------------------------------------------------------------

export * from '../../utils/ticketStatus.shared';
import { isDoneStatus, normalizeStatusForType } from '../../utils/ticketStatus.shared';

// ---------------------------------------------------------------------------
// P-19b — portal-only status helpers
// ---------------------------------------------------------------------------

/** The portal's label key (`uxPortal.requestStatus.<key>`) of a request status. */
export type RequestStatusKey = 'new' | 'inProgress' | 'waitingCustomer' | 'completed' | 'closed' | 'cancelled' | 'open';

/**
 * A request's status as the customer sees it (D-146: the customer sees the
 * service-request statuses themselves — חדשה / בטיפול / ממתינה ללקוח /
 * הושלמה / נסגרה). Legacy task steps fold into them; cancelled wins; an
 * unknown value is "open", never finished.
 */
export function requestStatusKey(status: string | null | undefined, cancelled: boolean): RequestStatusKey {
  if (cancelled) return 'cancelled';
  const s = status ? normalizeStatusForType(status, 'ticket') : status;
  switch (s) {
    case 'new':
    case 'open':
      return 'new';
    case 'in_progress':
      return 'inProgress';
    case 'waiting_customer':
    case 'waiting':
      return 'waitingCustomer';
    case 'completed':
    case 'resolved':
      return 'completed';
    case 'closed':
      return 'closed';
    default:
      return 'open';
  }
}

/** Tone of a request status key (same soft tint + dot as every status badge). */
export function requestStatusTone(key: string): 'neutral' | 'info' | 'warning' | 'success' {
  if (key === 'inProgress') return 'info';
  if (key === 'waitingCustomer') return 'warning';
  if (key === 'completed' || key === 'closed') return 'success';
  return 'neutral';
}

/** "הושלמה": the customer may answer (back to "בטיפול") or confirm the closing. */
export function awaitsCustomerConfirmation(status: string | null | undefined, cancelled: boolean): boolean {
  return requestStatusKey(status, cancelled) === 'completed';
}

/** Done for the portal lists (the server's DONE_STATUSES): "הושלמה", "נסגרה", or cancelled. */
export function isRequestFinished(status: string | null | undefined, cancelled: boolean): boolean {
  return cancelled || isDoneStatus(status);
}

export function parseApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'error' in err) {
    const e = (err as { error?: { message?: string } }).error;
    if (e?.message) return e.message;
  }
  return fallback;
}

/** Money through the shared Intl formatter; tolerates a missing or non-ISO currency value. */
export function money(v: number | string | null | undefined, currency?: string | null): string {
  const iso = currency && /^[A-Z]{3}$/.test(currency) ? currency : 'ILS';
  return formatCurrency(v, iso);
}

/**
 * QA-5 #5 — what is still owed on a charge: the server's remaining balance
 * (amount + adjustments − payments, the same billing_obligation_outstanding
 * as the debt / aging), never the original amount once a part was paid.
 */
export function openAmountOf(o: Pick<PortalObligation, 'amount' | 'outstanding' | 'payment_status'>): number {
  if (o.payment_status === 'paid') return 0;
  const v = o.outstanding ?? o.amount;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Display-only: an unpaid obligation whose due date has passed. */
export function isObligationOverdue(o: PortalObligation): boolean {
  return o.payment_status !== 'paid' && dueDayPassed(o.due_date); // DQ-445: by the Israeli day, like the server
}

/** P-19b (D-146 / Q-100): the i18n key of the customer's toast for one staff update. */
export function updateToastKey(u: { kind: 'reply' | 'completed' | 'status' | 'on_it' | 'conversation_started'; author_name: string | null }): string {
  if (u.kind === 'completed') return 'portalP19.toast.completed';
  // FIX-CONVPORTAL (D-210): added to a conversation.
  if (u.kind === 'conversation_started') return u.author_name ? 'portalP19.toast.conversationStarted' : 'portalP19.toast.conversationStartedNoName';
  return u.author_name ? 'portalP19.toast.reply' : 'portalP19.toast.replyNoName';
}
